export type StandardFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'extraordinary';

export interface FulfillmentAccessors<T> {
  /** Function to extract the unique template/blueprint ID from the record */
  getId: (item: T) => number | string | null | undefined;
  /** Function to extract the frequency string from the record */
  getFrequency: (item: T) => string | null | undefined;
  /** Function to extract the execution date string (YYYY-MM-DD or ISO string) from the record */
  getDate: (item: T) => string | null | undefined;
}

/**
 * A highly abstract utility that checks which entity IDs have been fulfilled
 * within their required chronological frequency windows relative to a baseline date.
 *
 * @param records Array of generic execution records.
 * @param referenceDateStr Baseline reference date in YYYY-MM-DD format.
 * @param accessors Configurable layout hooks to read IDs, dates, and frequencies polymorphically.
 * @returns A Set containing the IDs of all entities fulfilled in their active window.
 */
export const calculateFulfilledIdsInWindows = <T>(
  records: T[],
  referenceDateStr: string,
  accessors: FulfillmentAccessors<T>
): Set<string | number> => {
  const fulfilledSet = new Set<string | number>();
  if (!Array.isArray(records) || records.length === 0) return fulfilledSet;

  const baselineDate = new Date(referenceDateStr);
  const baselineYear = baselineDate.getFullYear();
  const baselineMonth = baselineDate.getMonth();

  // 1. Calculate Weekly Time-Window Bounds (Monday - Sunday)
  const currentDay = baselineDate.getDay();
  const distanceToMonday = currentDay === 0 ? -6 : 1 - currentDay;

  const monday = new Date(baselineDate);
  monday.setDate(baselineDate.getDate() + distanceToMonday);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  // 2. Calculate Monthly Time-Window Bounds
  const firstDayOfMonth = new Date(baselineYear, baselineMonth, 1, 0, 0, 0, 0);
  const lastDayOfMonth = new Date(baselineYear, baselineMonth + 1, 0, 23, 59, 59, 999);

  // 3. Calculate Yearly Time-Window Bounds
  const firstDayOfYear = new Date(baselineYear, 0, 1, 0, 0, 0, 0);
  const lastDayOfYear = new Date(baselineYear, 12, 0, 23, 59, 59, 999);

  // Cache timestamp values for O(1) loop evaluations
  const startOfWeek = monday.getTime();
  const endOfWeek = sunday.getTime();
  const startOfMonth = firstDayOfMonth.getTime();
  const endOfMonth = lastDayOfMonth.getTime();
  const startOfYear = firstDayOfYear.getTime();
  const endOfYear = lastDayOfYear.getTime();

  // 4. Process polymorphic rows iteratively
  for (let i = 0; i < records.length; i++) {
    const record = records[i];
    const id = accessors.getId(record);
    if (id == null) continue;

    const rawFreq = accessors.getFrequency(record) || '';
    const freq = rawFreq.toLowerCase().trim();

    const rawDate = accessors.getDate(record) || '';
    const dateStrOnly = rawDate.split('T')[0];
    if (!dateStrOnly) continue;

    const recordTime = new Date(dateStrOnly).getTime();

    switch (freq) {
      case 'daily':
      case 'giornaliero':
        if (dateStrOnly === referenceDateStr) {
          fulfilledSet.add(id);
        }
        break;

      case 'weekly':
      case 'settimanale':
        if (recordTime >= startOfWeek && recordTime <= endOfWeek) {
          fulfilledSet.add(id);
        }
        break;

      case 'monthly':
      case 'mensile':
        if (recordTime >= startOfMonth && recordTime <= endOfMonth) {
          fulfilledSet.add(id);
        }
        break;

      case 'yearly':
      case 'annuale':
        if (recordTime >= startOfYear && recordTime <= endOfYear) {
          fulfilledSet.add(id);
        }
        break;

      case 'extraordinary':
      case 'straordinario':
        fulfilledSet.add(id);
        break;

      default:
        break;
    }
  }

  return fulfilledSet;
};
